// public
import type React from 'react';
import { useState, useEffect } from 'react';

import type { PremiumFeaturesOverview } from '@xing-com/crate-common-graphql-types';

import type {
  ExtendersStateProps,
  featuresTableProps,
  generateTipsAndTricksActionUrlTrackingParamsType,
  generateUrlTrackingParamsType,
  onTabTrackingType,
  onTipsAndTricksButtonClickType,
  onTipsAndTricksButtonCloseClickType,
  onTipsAndTricksVideoTrackingType,
} from '../../types';
import { ButtonNavigation } from '../button-navigation';
import { FeaturesTable } from '../features-table';

type NavigationItem = {
  text: string | null | undefined;
  id: number;
};

// To fetch only the headers from featuresTable
const mapNavigationItems = (items: featuresTableProps): NavigationItem[] =>
  items.length > 1
    ? items.map((item, itemIndex) => ({
        text: item.header,
        id: itemIndex,
      }))
    : [];

export type FeaturesOverviewProps = {
  /**
   * array of strings representing the headers that the features table will use
   */
  featuresTableHeaders: string[] | React.ReactNode[];
  /**
   * array containing the data coming from the GraphQL request
   */
  featuresTable: featuresTableProps | PremiumFeaturesOverview['collection'];
  /**
   * hide buttons for actions such as View and TipsAndTricks
   */
  hideActions?: boolean;
  /**
   * hide only TipsAndTricks buttons
   */
  hideTipsAndTricks?: boolean;
  /**
   * callback for tab tracking
   */
  onTabTracking?: onTabTrackingType;
  /**
   * callback for TipsAndTricks button click (next to View button) tracking
   */
  onTipsAndTricksButtonClick?: onTipsAndTricksButtonClickType;
  /**
   * callback for TipsAndTricks close button (x) tracking
   */
  onTipsAndTricksButtonCloseClick?: onTipsAndTricksButtonCloseClickType;
  /**
   * callbacl for TipsAndTricks video tracking
   */
  onTipsAndTricksVideoTracking?: onTipsAndTricksVideoTrackingType;
  /**
   * callback for TipsAndTricks url tracking params of actions show in the right section of TipsAndTricks
   */
  generateTipsAndTricksActionUrlTrackingParams?: generateTipsAndTricksActionUrlTrackingParamsType;
  /**
   * callback for url tracking params of actions (View action typically)
   * these action buttons are hidden/shown through hideActions
   */
  generateUrlTrackingParams?: generateUrlTrackingParamsType;
};

export const FeaturesOverview: React.FC<FeaturesOverviewProps> = ({
  featuresTableHeaders,
  featuresTable,
  hideActions = true,
  hideTipsAndTricks = false,
  onTabTracking,
  onTipsAndTricksButtonClick,
  onTipsAndTricksButtonCloseClick,
  onTipsAndTricksVideoTracking,
  generateTipsAndTricksActionUrlTrackingParams,
  generateUrlTrackingParams,
}) => {
  const [activeTab, setActiveTab] = useState(0);
  const [openExtenders, setOpenExtenders] = useState<ExtendersStateProps>({});
  const buttonNavigationItems = mapNavigationItems(
    featuresTable as featuresTableProps
  );

  useEffect(() => {
    if (activeTab !== null && onTabTracking) {
      onTabTracking(activeTab);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  return (
    <div id="features-overview">
      {buttonNavigationItems.length > 0 ? (
        <ButtonNavigation
          itemsData={buttonNavigationItems}
          activeItemIndex={activeTab}
          onChange={(activeTabIndex) => {
            setActiveTab(activeTabIndex);
            setOpenExtenders({});
          }}
        />
      ) : null}
      <FeaturesTable
        data={featuresTable as featuresTableProps}
        activeTab={activeTab}
        openExtenders={openExtenders}
        setOpenExtenders={setOpenExtenders}
        featuresTableHeaders={featuresTableHeaders}
        hideActions={hideActions}
        hideTipsAndTricks={hideTipsAndTricks}
        onTipsAndTricksButtonClick={onTipsAndTricksButtonClick}
        onTipsAndTricksButtonCloseClick={onTipsAndTricksButtonCloseClick}
        onTipsAndTricksVideoTracking={onTipsAndTricksVideoTracking}
        generateTipsAndTricksActionUrlTrackingParams={
          generateTipsAndTricksActionUrlTrackingParams
        }
        generateUrlTrackingParams={generateUrlTrackingParams}
      />
    </div>
  );
};
